import React, {  useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Laptop, 
  ArrowRight, 
  CheckCircle, 
  TrendingUp, 
  Zap, 
  Users, 
  BarChart3,
  Clock,
  Target,
  Eye,
  Play,
  Code,
  Cpu,
  Cloud
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';

const TechnologyITServicesPage: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  
  

  const challenges = [
    'Managing complex software development projects across distributed teams',
    'Scaling operations and maintaining quality in fast-growing tech companies',
    'Ensuring efficient resource allocation and project timeline management',
    'Building robust HR systems for tech talent acquisition and retention',
    'Creating responsive and scalable web platforms for clients and internal use',
    'Adapting to rapidly changing technology landscapes and client requirements'
  ];

  const solutions = [
    {
      name: 'ManageRTC',
      path: '/products/it/ManageRTC',
      description: 'Centralize and control your real-time voice, video, and messaging.',
      benefits: ['Cost analytics', 'Policy routing', 'QoS monitoring']
    },
    {
      name: 'Websites',
      path: '/products/it/Websites',
      description: 'Custom e-commerce platforms and retail websites designed for optimal customer experience.',
      benefits: ['E-commerce Platforms', 'Customer Portals', 'Mobile Commerce']
    },
    // {
    //   name: 'Project Management Tool',
    //   path: '/products/it#project-management-tool',
    //   description: 'Comprehensive project coordination for software development and IT service delivery projects.',
    //   benefits: ['Agile Development', 'Resource Planning', 'Client Management']
    // }
  ];

  const keyFeatures = [
    {
      icon: Code,
      title: 'Developer-Centric Design',
      description: 'Solutions built by developers for developers, understanding the unique needs of tech teams and workflows.'
    },
    {
      icon: Zap,
      title: 'Agile & Scalable',
      description: 'Flexible systems that adapt to agile methodologies and scale with your growing technology business.'
    },
    {
      icon: Cloud,
      title: 'Cloud-Native Architecture',
      description: 'Modern, cloud-first solutions that leverage the latest technologies and best practices.'
    },
    {
      icon: Target,
      title: 'Innovation-Focused',
      description: 'Cutting-edge tools and platforms that enable innovation and competitive advantage in tech markets.'
    }
  ];

  const benefits = [
    {
      icon: Clock,
      title: 'Development Velocity',
      description: 'Streamlined workflows and intelligent project management significantly accelerate development cycles.',
      metric: '60% faster delivery'
    },
    {
      icon: Users,
      title: 'Team Productivity',
      description: 'Advanced collaboration tools and resource management boost team productivity and satisfaction.',
      metric: '75% productivity gain'
    },
    {
      icon: BarChart3,
      title: 'Project Success Rate',
      description: 'Comprehensive project management and analytics dramatically improve project success rates.',
      metric: '85% success rate'
    },
    {
      icon: TrendingUp,
      title: 'Business Growth',
      description: 'Efficient operations and quality delivery enable sustainable business growth and client satisfaction.',
      metric: '90% client retention'
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 10 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-3 h-3 bg-cyan-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 3,
                delay: i * 0.2,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-linear-to-br from-cyan-500 to-blue-600 neon-glow">
                  <Laptop className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-cyan-400 uppercase tracking-wide">Next-Gen Technology Solutions</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    Technology & <span className="bg-linear-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent">IT Services</span>
                  </h1>
                </div>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-2xl font-semibold mb-4 text-cyan-300"
              >
                Empowering Tech Companies with Intelligent Solutions
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed"
              >
                The technology and IT services industry moves at breakneck speed, where innovation, agility, and efficient 
                delivery are the keys to success. From startups to enterprise software companies, organizations need systems 
                that can scale with growth, manage distributed teams, and deliver exceptional client experiences. At amasQIS.ai, 
                we understand the unique challenges of tech companies and provide purpose-built solutions for development teams, 
                project management, and digital infrastructure.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-linear-to-r from-cyan-500 to-blue-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300"
                >
                  Accelerate Tech
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative perspective-1000"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  className="absolute -inset-10 bg-linear-to-r from-cyan-500 to-blue-600 opacity-20 rounded-full blur-3xl"
                />
                
                <motion.div
                  whileHover={{ scale: 1.05, rotateY: 5, rotateX: 5 }}
                  transition={{ duration: 0.4 }}
                  className="relative z-10 glass-strong rounded-2xl overflow-hidden transform-gpu"
                >
                  <ImageWithFallback
                    src='/assets/Industries/it-services.png'
                    alt="Technology & IT Services Industry"
                    className="w-full h-96 object-cover"
                  />
                  
                  <div className="absolute inset-0 bg-linear-to-t from-black/80 via-transparent to-transparent">
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="flex items-center gap-3 text-white">
                        <div className="p-2 bg-cyan-500/20 rounded-lg backdrop-blur-sm">
                          <Laptop className="w-5 h-5 text-cyan-500" />
                        </div>
                        <div>
                          <p className="font-semibold">AI-Powered</p>
                          <p className="text-sm text-white/80">Tech Solutions</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1 }}
                  className="absolute -top-4 -right-4 glass p-3 rounded-xl"
                >
                  <div className="flex items-center gap-2 text-cyan-500">
                    <Cpu className="w-4 h-4 animate-pulse" />
                    <span className="text-xs font-mono">TECH</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Challenges in Technology & IT Services
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Tech companies face unique operational challenges that require specialized solutions for team management, project delivery, and scaling operations.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {challenges.map((challenge, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300"
              >
                <div className="flex items-start gap-3">
                  <Code className="w-5 h-5 text-cyan-400 mt-0.5 shrink-0" />
                  <p className="text-muted-foreground">{challenge}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Our Solutions for Technology & IT Services
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Purpose-built technology solutions designed to accelerate development, optimize team performance, and scale tech operations.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <Link
                key={solution.name}
                to={solution.path}
                className="block group h-full"
              >
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300 cursor-pointer h-full flex flex-col"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-semibold text-cyan-400 group-hover:text-cyan-300 transition-colors">
                      {solution.name}
                    </h3>
                    <ArrowRight className="w-5 h-5 text-cyan-400 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <p className="text-muted-foreground mb-4">{solution.description}</p>
                  <div className="space-y-2">
                    {solution.benefits.map((benefit) => (
                      <div key={benefit} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-400" />
                        <span className="text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Why Tech Companies Choose amasQIS.ai
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              We speak the language of technology and understand the fast-paced, innovation-driven culture of tech companies.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300 text-center"
                >
                  <div className="mb-4 flex justify-center">
                    <Icon className="w-8 h-8 text-cyan-500" />
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Measurable Technology Impact
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Transform your technology operations with quantifiable improvements in development velocity, team productivity, and project success.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-cyan-500/20">
                      <Icon className="w-6 h-6 text-cyan-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                      <p className="text-muted-foreground mb-3">{benefit.description}</p>
                      <div className="text-blue-400 font-semibold">{benefit.metric}</div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-15">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready to Accelerate Your Tech Operations?
            </h2>
            <p className="text-lg text-muted-foreground text-center mb-8">
              Join innovative tech companies that trust amasQIS.ai to deliver cutting-edge solutions for development teams and IT operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-3 bg-linear-to-r from-cyan-500 to-blue-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300"
              >
                Accelerate Tech
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/solutions"
                className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300"
              >
                Explore Solutions
                <Eye className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export { TechnologyITServicesPage };
